import React, { useCallback, useContext } from 'react';
import { isArray } from 'lodash/lang';

import { FinBudgetEventApi } from '../../../../../../../../api';
import ModalBoxes from '../../../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import { MANAGE_BUDGETS } from '../../../../../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../../../../../services/auth';
import { toBill } from '../../../../../../../../services/financial';
import { onRequestError } from '../../../../../../../../services/handlers';
import { eventTypes } from '../../../budgetConstant';
import { BudgetDetailsContext } from '../../../BudgetDetailsContext';
import { modalTypes } from '../TableConstants';

export const BudgetEventActionsButton = ({ row }) => {
  const {
    state: { budget, isEditingForbidden, currentStudyBudget, vendors, events },
    budgetUniqueIdentifier: budgetId,
    loadBudgetEvents
  } = useContext(BudgetDetailsContext);

  const removeBudgetEvent = useCallback(
    budgetEvent => {
      ModalBoxes.confirm({
        content: 'Are you sure you want to delete this record?',
        confirmButton: 'Yes',
        cancelButton: 'No'
      }).then(
        () => {
          FinBudgetEventApi.inactivateBudgetEvent(budgetId, budgetEvent.id).then(loadBudgetEvents, onRequestError);
        },
        () => {}
      );
    },
    [budgetId, loadBudgetEvents]
  );

  function resolveModalTitle(budgetEventType, title) {
    switch (budgetEventType) {
      case 'EXPENSE_FIXED':
        return 'Edit Expense - Fixed budget event';
      case 'PATIENT_REIMBURSEMENT':
        return 'Edit Patient Reimbursement budget event';
      case 'ITEM_GROUP_FIELD':
        return 'Edit Item Group Field budget event';
      case 'ENCOUNTER_ITEM_GROUP_FIELD':
      case 'SITUATIONAL_ITEM_GROUP_FIELD':
      case 'UNEXPECTED_ITEM_GROUP_FIELD':
        return 'Edit Encounter Item Group Field budget event';
      default:
        return title;
    }
  }

  function resolveClassName(budgetEventType, componentClassName) {
    switch (budgetEventType) {
      case eventTypes.ENCOUNTER_ITEM_GROUP_FIELD.BudgetRowType:
      case eventTypes.SITUATIONAL_ITEM_GROUP_FIELD.BudgetRowType:
      case eventTypes.UNEXPECTED_ITEM_GROUP_FIELD.BudgetRowType:
        return 'edit-encounter-item-group-field-modal';
      default:
        return componentClassName;
    }
  }

  const groupStatusEventsAndBuildData = useCallback(
    budgetEvent => {
      const filteredItems = events
        .filter(event => event.groupId === budgetEvent?.groupId)
        .sort((a, b) => a.countFrom - b.countFrom);
      if (isArray(filteredItems) && filteredItems.length) {
        const {
          name,
          overhead,
          withholding,
          managementFee,
          encounterOverride,
          nonInvoiceable,
          encounterId,
          encounterName,
          triggerName,
          vendorId,
          patientStatusId,
          groupId,
          secondaryPatientStatusId,
          primaryStatusRatio,
          secondaryStatusRatio
        } = filteredItems[0];
        const eventRows = filteredItems.map(event => {
          return {
            id: event.id,
            countFrom: event.countFrom,
            countTo: event.countTo ? event.countTo : Number.MAX_SAFE_INTEGER,
            clientAmount: toBill(event.clientAmount, false),
            siteAmount: toBill(event.siteAmount, false),
            vendorAmount: toBill(event.vendorAmount, false),
            vendorAmountType: event.vendorAmountType,
            siteAmountType: event.siteAmountType,
            encounterId: event.encounterId,
            encounterName: event.encounterName,
            name: event.name,
            triggerName: event.triggerName,
            vendorId: event.vendorId,
            patientStatusId: event.patientStatusId,
            groupId: event.groupId,
            secondaryPatientStatusId: event.secondaryPatientStatusId,
            primaryStatusRatio: event.primaryStatusRatio,
            secondaryStatusRatio: event.secondaryStatusRatio
          };
        });
        return {
          eventRows,
          name,
          overhead,
          withholding,
          managementFee,
          encounterOverride,
          nonInvoiceable,
          encounterId,
          encounterName,
          triggerName,
          vendorId,
          patientStatusId,
          groupId,
          secondaryPatientStatusId,
          primaryStatusRatio,
          secondaryStatusRatio
        };
      }
      return {};
    },
    [events]
  );
  const prepareEventData = useCallback(
    budgetRow => {
      if (budgetRow.type === 'EXPENSE_FIXED') {
        const groupId = budgetRow.groupId;
        const vendorId = budgetRow.vendorId;
        const groupRows = events.filter(event => event.groupId === groupId).sort((a, b) => a.countFrom - b.countFrom);
        if (isArray(groupRows) && groupRows.length) {
          const { name, overhead, withholding, managementFee, encounterOverride, nonInvoiceable } = groupRows[0];
          const eventRows = groupRows.map(groupRow => {
            return {
              id: groupRow.id,
              countFrom: groupRow.countFrom,
              countTo: groupRow.countTo ? groupRow.countTo : Number.MAX_SAFE_INTEGER,
              clientAmount: toBill(groupRow.clientAmount, false),
              siteAmount: toBill(groupRow.siteAmount, false),
              vendorAmount: toBill(groupRow.vendorAmount, false),
              vendorAmountType: groupRow.vendorAmountType,
              siteAmountType: groupRow.siteAmountType
            };
          });
          return {
            groupId,
            eventRows,
            name,
            overhead,
            withholding,
            managementFee,
            encounterOverride,
            nonInvoiceable,
            vendorId
          };
        }
        return {};
      } else if (
        [
          eventTypes.ENCOUNTER.BudgetRowType,
          eventTypes.UNEXPECTED_ENCOUNTER.BudgetRowType,
          eventTypes.SITUATIONAL_ENCOUNTER.BudgetRowType,

          eventTypes.ENCOUNTER_ITEM_GROUP.BudgetRowType,
          eventTypes.UNEXPECTED_ITEM_GROUP.BudgetRowType,
          eventTypes.SITUATIONAL_ITEM_GROUP.BudgetRowType,

          eventTypes.ENCOUNTER_ITEM_GROUP_FIELD.BudgetRowType,
          eventTypes.UNEXPECTED_ITEM_GROUP_FIELD.BudgetRowType,
          eventTypes.SITUATIONAL_ITEM_GROUP_FIELD.BudgetRowType,

          eventTypes.ITEM_GROUP_FIELD.BudgetRowType,
          eventTypes.ITEM_GROUP_GENERAL.BudgetRowType
        ].includes(budgetRow.type)
      ) {
        const {
          groupId,
          triggerId,
          triggerName,
          name,
          overhead,
          withholding,
          managementFee,
          excludedFromRevenueProjection,
          nonInvoiceable,
          vendorId
        } = budgetRow;

        const eventRows = events
          .filter(event => event.groupId === groupId)
          .sort((a, b) => a.countFrom - b.countFrom)
          .map(groupRow => {
            return {
              id: groupRow.id,
              countFrom: groupRow.countFrom,
              countTo: groupRow.countTo ? groupRow.countTo : Number.MAX_SAFE_INTEGER,
              clientAmount: toBill(groupRow.clientAmount, false),
              siteAmount: toBill(groupRow.siteAmount, false),
              vendorAmount: toBill(groupRow.vendorAmount, false),
              vendorAmountType: groupRow.vendorAmountType,
              siteAmountType: groupRow.siteAmountType
            };
          });

        return {
          groupId,
          triggerId,
          triggerName,
          name,
          overhead,
          withholding,
          managementFee,
          excludedFromRevenueProjection,
          nonInvoiceable,
          vendorId,
          eventRows
        };
      } else {
        return budgetRow;
      }
    },
    [events]
  );

  const showEditBudgetEvent = useCallback(
    (Component, budgetEvent) => {
      if (['STATUS_CHANGE', 'RATIO_STATUS_CHANGE', 'ENCOUNTER_STATUS_CHANGE'].includes(budgetEvent.type)) {
        const modalBox = ModalBoxes.open({
          title: Component.title,
          component: (
            <Component
              triggerType={budgetEvent.type}
              budgetEventType={budgetEvent.type}
              studyId={budget?.studyId}
              managementFeePercent={budget?.managementFee}
              id={budgetId}
              data={groupStatusEventsAndBuildData(budgetEvent)}
              isEditingForbidden={isEditingForbidden}
              currentStudyBudget={currentStudyBudget}
              onSave={(data, groupId) => {
                if (budgetEvent.type === 'STATUS_CHANGE') {
                  FinBudgetEventApi.updateStatusChangeEvent(budgetId, groupId, data).then(
                    loadBudgetEvents,
                    onRequestError
                  );
                } else if (budgetEvent.type === 'RATIO_STATUS_CHANGE') {
                  FinBudgetEventApi.updateRatioStatusChangeEvent(budgetId, groupId, data).then(
                    loadBudgetEvents,
                    onRequestError
                  );
                } else {
                  FinBudgetEventApi.updateEncounterStatusChanges(budgetId, groupId, data).then(
                    loadBudgetEvents,
                    onRequestError
                  );
                }
                modalBox.close();
              }}
            />
          )
        });
      } else if (budgetEvent.type === 'EXPENSE_VARIABLE') {
        ModalBoxes.open({
          title: 'Edit Expense - Variable budget event',
          component: (
            <Component
              currentStudyBudget={currentStudyBudget}
              managementFeePercent={budget?.managementFee}
              saveEvent={data => {
                return FinBudgetEventApi.updateExpenseVariableEvent(budgetId, budgetEvent.groupId, data).then(
                  loadBudgetEvents,
                  onRequestError
                );
              }}
              eventForUpdate={budgetEvent}
            />
          ),
          size: 'w800',
          className: 'expense-variable-modal'
        });
      } else {
        const modalBox = ModalBoxes.open({
          title: resolveModalTitle(budgetEvent.type, Component.title),
          className: resolveClassName(budgetEvent.type, Component.className),
          size: 'w800',
          component: (
            <Component
              triggerType={budgetEvent.type}
              budgetEventType={budgetEvent.type}
              id={budgetId}
              studyId={budget?.studyId}
              managementFeePercent={budget?.managementFee}
              currentStudyBudget={currentStudyBudget}
              data={prepareEventData(budgetEvent)}
              vendors={vendors}
              onSave={(data, groupId) => {
                switch (budgetEvent.type) {
                  case 'EXPENSE_FIXED':
                    FinBudgetEventApi.updateExpenseFixedEvent(budgetId, groupId, data).then(
                      loadBudgetEvents,
                      onRequestError
                    );
                    break;
                  case 'ITEM_GROUP_GENERAL':
                    FinBudgetEventApi.updateItemGroupEvent(budgetId, groupId, data).then(
                      loadBudgetEvents,
                      onRequestError
                    );
                    break;
                  case 'PATIENT_REIMBURSEMENT':
                    FinBudgetEventApi.updatePatientReimbursementEvent(budgetId, budgetEvent.id, data).then(
                      loadBudgetEvents,
                      onRequestError
                    );
                    break;
                  case eventTypes.ENCOUNTER.BudgetRowType:
                  case eventTypes.UNEXPECTED_ENCOUNTER.BudgetRowType:
                  case eventTypes.SITUATIONAL_ENCOUNTER.BudgetRowType:
                    FinBudgetEventApi.updateEncounterEvent(budgetId, groupId, data).then(
                      loadBudgetEvents,
                      onRequestError
                    );
                    break;
                  case eventTypes.ENCOUNTER_ITEM_GROUP.BudgetRowType:
                  case eventTypes.UNEXPECTED_ITEM_GROUP.BudgetRowType:
                  case eventTypes.SITUATIONAL_ITEM_GROUP.BudgetRowType:
                    FinBudgetEventApi.updateEncounterItemgroupEvent(budgetId, groupId, data).then(
                      loadBudgetEvents,
                      onRequestError
                    );
                    break;
                  case eventTypes.ENCOUNTER_ITEM_GROUP_FIELD.BudgetRowType:
                  case eventTypes.UNEXPECTED_ITEM_GROUP_FIELD.BudgetRowType:
                  case eventTypes.SITUATIONAL_ITEM_GROUP_FIELD.BudgetRowType:
                    FinBudgetEventApi.updateEncounterItemGroupFieldEvent(budgetId, groupId, data).then(
                      loadBudgetEvents,
                      onRequestError
                    );
                    break;
                  case eventTypes.ITEM_GROUP_FIELD.BudgetRowType:
                    FinBudgetEventApi.updateItemGroupFieldEvent(budgetId, groupId, data).then(
                      loadBudgetEvents,
                      onRequestError
                    );
                    break;
                  default:
                    FinBudgetEventApi.updatePatientStipendEvent(budgetId, budgetEvent.id, data).then(
                      loadBudgetEvents,
                      onRequestError
                    );
                }
                modalBox.close();
              }}
            />
          )
        });
      }
    },
    [
      budget?.studyId,
      budget?.managementFee,
      budgetId,
      groupStatusEventsAndBuildData,
      isEditingForbidden,
      currentStudyBudget,
      loadBudgetEvents,
      prepareEventData,
      vendors
    ]
  );

  const visibleMode = (
    <span
      style={{ cursor: 'pointer' }}
      className="material-icons pr-2 c-p protocol-setup-btn"
      onClick={() => showEditBudgetEvent(modalTypes[row.type], row)}
    >
      visibility
    </span>
  );
  const editMode = (
    <div>
      <span
        className="glyphicon glyphicon-edit pr-2 c-p protocol-setup-btn"
        onClick={() => showEditBudgetEvent(modalTypes[row.type], row)}
      />
      <span className="glyphicon glyphicon-remove c-p protocol-setup-btn" onClick={() => removeBudgetEvent(row)} />
    </div>
  );
  return isEditingForbidden || !userHasAccessTo(MANAGE_BUDGETS) ? visibleMode : editMode;
};
